import type { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { ExternalLink } from '@uikit';

import type { ItemsProps, ProjectContentTableOfContentTab } from '../types';
import { gallery } from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
  withNumericSymbols?: boolean;
}

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          Welcome to <strong>Metropolis</strong>, an ecosystem merging gamified commerce,
          cutting-edge AI technology, and culture into a vibrant virtual hub where the boundaries
          between digital and physical realities blur.
        </p>
        <p className="max-sm:text-sm font-normal">
          Its core platform, <strong>Metropolis X</strong>, revolutionizes social commerce, offering
          a gamified marketplace where digital collectibles unlock real-world assets and
          experiences, supported by an expansive network of over 400 top creators and brands, and
          powered by your digital identity.
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          Meanwhile, <strong>Metropolis World</strong> allows you to own properties, run businesses,
          experience AI-generated quests, and participate in the greater network economy of this
          tangible virtual city.
        </p>
        <div className="overflow-hidden mb-6">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt={`metropolice-app-${index}`}
                  width={1920}
                  height={1080}
                  quality={100}
                  className="w-full h-auto select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <p className="max-sm:text-sm font-normal">
          To learn more about Metropolis, visit the{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://metropolisx.io/metropolis-overview"
          >
            overview
          </ExternalLink>{' '}
          and{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://metropolisworld.io/data/whitepaper/metropolis_world_whitepaper.pdf"
          >
            whitepaper
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'traction-and-achievements',
    title: 'Traction and Achievements',
    content: (
      <>
        <ImageProxy
          alt="Traction and Achievements"
          src="/launchpad/stories/metropolis/achivements.png"
          className="block w-full h-auto mt-12 mb-6"
          quality={100}
          width={775}
          height={435}
        />
        <p className="max-sm:text-sm font-normal">
          For detailed updates, check out the latest{' '}
          <ExternalLink
            className="underline text-base-text-blue"
            href="https://docs.google.com/document/d/1gtRdAxmCUMP1xNz4jp_TBdNRXZpx-4p1wwRvie3-tsQ/edit?tab=t.0"
          >
            Community Update
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'investors-and-partners',
    title: 'Investors and Partners',
    content: (
      <>
        <ImageProxy
          alt="Investors and Partners"
          src="/launchpad/stories/metropolis/partners.png"
          className="block w-full h-auto mt-12 mb-6"
          quality={100}
          width={775}
          height={435}
        />
        <p className="max-sm:text-sm font-normal">
          For the full investor and partner list, visit{' '}
          <ExternalLink
            className="underline text-base-text-blue"
            href="https://metropolisworld.io/partners"
          >
            Metropolis Partners
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          Metropolis employs a multifaceted business model designed to sustain growth and provide
          value across its ecosystem. The platform generates revenue through initial property sales,
          tiered access, and ongoing utility upgrades, while leveraging the $CLAY token for in-game
          transactions, staking rewards, and bridging digital collectibles with real-world assets.
          Digital collectibles and NFTs, supported by a robust marketplace, offer gameplay utility
          and long-term asset value. Creator-driven quests and 3D commerce hubs empower creators and
          brands while contributing to Metropolis’ revenue via transaction and hosting fees.
        </p>
        <p className="max-sm:text-sm font-normal">
          The model integrates real-world assets (RWA) with NFTs, enabling unique value propositions
          and premium brand partnerships. Subscription services and premium memberships drive
          recurring revenue and enhanced user engagement. Marketplace fees and brand sponsorships
          further diversify income streams, creating opportunities for co-branded experiences,
          product placements, and virtual events. This comprehensive approach ensures a dynamic,
          innovative ecosystem that benefits users, creators, and stakeholders alike.
        </p>
      </>
    ),
  },
  {
    id: 'tokenomics-and-token-utility',
    title: 'Tokenomics and Token Utility',
    content: (
      <>
        <ImageProxy
          alt="Token Utility"
          src="/launchpad/stories/metropolis/utility.png"
          className="block w-full h-auto mt-12 mb-6"
          quality={100}
          width={775}
          height={435}
        />
        <ImageProxy
          alt="Tokenomics"
          src="/launchpad/stories/metropolis/tokenomics.png"
          className="block w-full h-auto mb-6"
          quality={100}
          width={775}
          height={435}
          resizeType="fit"
        />
        <p className="max-sm:text-sm font-normal">
          To learn more about the Metropolis Business Model, the $CLAY token, and its utility, visit{' '}
          <ExternalLink
            className="underline text-base-text-blue text-nowrap"
            href="https://metropolisworld.io/clay"
          >
            Powered by Clay
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <ImageProxy
          alt="Team"
          src="/launchpad/stories/metropolis/team.png"
          className="block w-full h-auto mt-12 mb-6"
          quality={100}
          width={775}
          height={435}
        />
        <p className="max-sm:text-sm font-normal">
          To explore the full team, visit{' '}
          <ExternalLink
            className="underline text-base-text-blue"
            href="https://metropolisworld.io/team"
          >
            Metropolis Team
          </ExternalLink>
          .
        </p>
      </>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <>
        <ImageProxy
          alt="Roadmap"
          src="/launchpad/stories/metropolis/roadmap.png"
          className="block w-full h-auto mt-12 mb-2"
          quality={100}
          width={775}
          height={435}
        />
      </>
    ),
  },
];
